import { useState, useEffect } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Stars, StatusBadge, Th, Td, Empty, pct } from "../components/shared";
import { inr, fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import ExportMenu from "../components/ExportMenu";
import { toast } from "sonner";
import { Plus, Download, Store, Phone, Mail, X, Pencil } from "lucide-react";

const emptyForm = { vendor_name: "", category: "Consumables", contact_person: "", phone: "", email: "", branches_served: [], rating: 4, payment_terms: "Net 30", notes: "" };

export default function Vendors() {
  const { meta } = useApp();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [profile, setProfile] = useState(null);

  const load = () => api.get("/vendors").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const toggleBranch = (b) => setForm((f) => ({ ...f, branches_served: f.branches_served.includes(b) ? f.branches_served.filter((x) => x !== b) : [...f.branches_served, b] }));

  const openCreate = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (v) => {
    setEditId(v.id);
    setForm({ vendor_name: v.vendor_name, category: v.category, contact_person: v.contact_person || "", phone: v.phone || "", email: v.email || "", branches_served: v.branches_served || [], rating: v.rating, payment_terms: v.payment_terms || "", notes: v.notes || "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.vendor_name) { toast.error("Vendor name required"); return; }
    if (editId) { await api.put(`/vendors/${editId}`, { ...form, rating: Number(form.rating) }); toast.success("Vendor updated"); }
    else { await api.post("/vendors", { ...form, rating: Number(form.rating) }); toast.success("Vendor added"); }
    setOpen(false); setEditId(null); setForm(emptyForm); load();
  };

  const openProfile = async (id) => { const r = await api.get(`/vendors/${id}`); setProfile(r.data); };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Master Vendor Directory</h1>
          <p className="text-brand-slate text-sm mt-1">Supplier profiles, ratings & performance</p>
        </div>
        <div className="flex gap-2">
          <ExportMenu dataset="vendors" testid="export-vendors" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-vendor-button" onClick={openCreate} className="gap-2 bg-brand-teal hover:bg-brand-tealdark active:scale-95 transition-transform"><Plus className="w-4 h-4" /> Add Vendor</Button></DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
                <DialogDescription>Supplier details, rating and branches served. Order stats are computed from the log.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Vendor Name</Label><Input data-testid="vendor-name" value={form.vendor_name} onChange={(e) => setForm((f) => ({ ...f, vendor_name: e.target.value }))} /></div>
                <div><Label>Primary Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Rating</Label>
                  <Select value={String(form.rating)} onValueChange={(v) => setForm((f) => ({ ...f, rating: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n} ★</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm((f) => ({ ...f, contact_person: e.target.value }))} /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div>
                <div className="col-span-2"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
                <div className="col-span-2"><Label>Payment Terms</Label><Input value={form.payment_terms} onChange={(e) => setForm((f) => ({ ...f, payment_terms: e.target.value }))} /></div>
                <div className="col-span-2"><Label>Branches Served</Label>
                  <div className="flex flex-wrap gap-2 mt-1">{meta.branches.map((b) => (
                    <button type="button" key={b} onClick={() => toggleBranch(b)} className={`text-xs px-3 py-1.5 rounded-full border ${form.branches_served.includes(b) ? "bg-brand-teal text-white border-brand-teal" : "border-border text-brand-slate"}`}>{b}</button>
                  ))}</div></div>
                <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="vendor-submit" onClick={submit} className="bg-brand-teal hover:bg-brand-tealdark">{editId ? "Save Changes" : "Add Vendor"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border"><tr>
              {["Vendor", "Category", "Contact", "Branches", "Rating", "Terms", "Orders", "Lifetime Spend", "On-Time %", ""].map((h) => <Th key={h}>{h}</Th>)}
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={10}><Empty icon={Store} msg="No vendors" /></td></tr> :
                rows.map((v) => (
                  <tr key={v.id} data-testid="vendor-row" onClick={() => openProfile(v.id)} className="border-b border-border/60 hover:bg-slate-50 cursor-pointer transition-colors">
                    <Td className="font-medium">{v.vendor_name}</Td>
                    <Td className="text-slate-600">{v.category}</Td>
                    <Td className="text-slate-600">{v.contact_person}</Td>
                    <Td className="text-slate-600 text-xs">{v.branches_served?.join(", ")}</Td>
                    <Td><Stars rating={v.rating} /></Td>
                    <Td className="text-slate-600">{v.payment_terms}</Td>
                    <Td className="tabular-nums">{v.total_orders}</Td>
                    <Td className="tabular-nums font-semibold">{inr(v.lifetime_spend)}</Td>
                    <Td className={v.on_time_rate != null && v.on_time_rate < 0.8 ? "text-rose-600" : "text-brand-emerald"}>{v.on_time_rate == null ? "—" : pct(v.on_time_rate)}</Td>
                    <Td><button data-testid="edit-vendor" onClick={(e) => { e.stopPropagation(); openEdit(v); }} className="text-slate-400 hover:text-brand-teal"><Pencil className="w-4 h-4" /></button></Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!profile} onOpenChange={(o) => !o && setProfile(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vendor-profile">
          {profile && <>
            <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">{profile.vendor_name} <Stars rating={profile.rating} /></DialogTitle>
              <DialogDescription>Performance summary and full supply history for this vendor.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["Orders", profile.total_orders], ["Lifetime Spend", inr(profile.lifetime_spend)], ["On-Time %", profile.on_time_rate == null ? "—" : pct(profile.on_time_rate)], ["Terms", profile.payment_terms]].map(([l, val]) => (
                <div key={l} className="bg-slate-50 rounded-lg p-3"><div className="text-[10px] uppercase tracking-widest text-brand-slate">{l}</div><div className="font-heading font-bold text-navy mt-1">{val}</div></div>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-brand-slate">
              {profile.contact_person && <span>{profile.contact_person}</span>}
              {profile.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {profile.phone}</span>}
              {profile.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {profile.email}</span>}
            </div>
            {profile.notes && <p className="text-sm bg-amber-50 border border-amber-100 rounded-lg p-3 text-slate-700">{profile.notes}</p>}
            <h4 className="font-heading font-semibold text-navy mt-2">Supply History ({profile.history?.length || 0})</h4>
            <div className="overflow-x-auto border border-border rounded-lg">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-border"><tr>{["Item", "Branch", "Qty", "Unit Price", "Total", "Delay", "Status"].map((h) => <Th key={h}>{h}</Th>)}</tr></thead>
                <tbody>
                  {profile.history?.map((h) => (
                    <tr key={h.id} className="border-b border-border/60">
                      <Td className="max-w-[180px] truncate">{h.item_name}</Td>
                      <Td className="text-slate-600">{h.branch}</Td>
                      <Td>{h.qty}</Td>
                      <Td className="tabular-nums">{inr(h.final_unit_price)}</Td>
                      <Td className="tabular-nums font-semibold">{inr(h.total_final)}</Td>
                      <Td className={h.delay_days > 0 ? "text-rose-600" : "text-slate-600"}>{h.delay_days == null ? "—" : `${h.delay_days}d`}</Td>
                      <Td><StatusBadge value={h.order_status} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
